import { useNavigate } from 'react-router-dom'
import { useMediaQuery } from 'usehooks-ts'
import { AnimatePresence, motion } from 'framer-motion'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { Box, Text } from '@factorialco/deprecated-design-system'
import { F0Icon, F0Button, F0TagRaw } from '@factorialco/f0-react'
import { useAiChat } from 'modules/ai/chat/useAiChat'
import { Check, ExternalLink } from '@factorialco/f0-react/icons/app'
import i18n from 'modules/core/lib/i18n'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { getTaskActionUrl } from 'modules/get_started/lib/taskCatalog'
import type { Task } from 'modules/get_started/lib/taskCatalog'
import VideoThumbnail from 'modules/get_started/components/TaskItem/_VideoThumbnail'
import FallbackPlaceholder from 'modules/get_started/components/TaskItem/_FallbackPlaceholder'
import { useVoiceSession } from 'modules/ai_voice/providers/voiceSession'

type Props = {
  task: Task
  isCompleted?: boolean
  onSkip?: (taskKey: string) => void
  isExpanded?: boolean
  onToggle?: () => void
  aiToursEnabled: boolean
}

const TaskItem = ({
  task,
  isCompleted = false,
  onSkip,
  isExpanded = false,
  onToggle,
  aiToursEnabled,
}: Props) => {
  const navigate = useNavigate()
  const { navigation } = useNavigation()
  const { setParams, startSession } = useVoiceSession()
  const { enabled: aiEnabled, sendMessage, setOpen: setAiChatOpen, setMode } = useAiChat()
  const isSmallScreen = useMediaQuery('(max-width: 1600px)')
  const actionUrl = getTaskActionUrl(task.taskKey, navigation)
  const effectiveTourId = aiToursEnabled && task.enableVoiceTour ? task.taskKey : null

  const handleStartSetup = () => {
    track(trackingEvents.getStarted.clickStartSetup, {
      task_key: task.taskKey,
      tour_id: effectiveTourId,
      ai_tours_enabled: aiToursEnabled,
      action_url: actionUrl,
      is_completed: isCompleted,
    })
    if (effectiveTourId) {
      setParams({ agent: 'onboarding', task: effectiveTourId })
      startSession()
    } else if (actionUrl) {
      navigate(actionUrl)
      if (aiEnabled && task.aiQuestion) {
        setAiChatOpen(true)
        sendMessage(task.aiQuestion)
      }
    }
  }

  const handleLearnMore = () => {
    track(trackingEvents.getStarted.clickLearnMore, {
      task_key: task.taskKey,
      tour_id: effectiveTourId,
      ai_enabled: aiEnabled,
      help_center_url: task.helpCenterUrl,
      is_completed: isCompleted,
    })
    if (aiEnabled && task.aiQuestion && (effectiveTourId || !actionUrl)) {
      setMode('chat')
      setAiChatOpen(true)
      sendMessage(task.aiQuestion)
    } else if (task.helpCenterUrl) {
      window.open(task.helpCenterUrl, '_blank')
    }
  }

  const handleSkip = () => {
    track(trackingEvents.getStarted.clickSkip, {
      task_key: task.taskKey,
      tour_id: effectiveTourId,
      action_url: actionUrl,
      is_completed: isCompleted,
    })
    onSkip?.(task.taskKey)
  }

  return (
    <Box
      className={
        isExpanded
          ? 'bg-f1-background-secondary rounded-lg transition-colors duration-200'
          : 'rounded-lg transition-colors duration-200 hover:bg-f1-background-tertiary'
      }
    >
      <Box
        flexDirection="column"
        {...(isExpanded
          ? {
              paddingTop: 's4',
              paddingBottom: 's16',
              paddingLeft: 's12',
              paddingRight: 's12',
              gap: 's4',
            }
          : { padding: 's8' })}
      >
        <Box
          flexDirection="row"
          alignItems="center"
          justifyContent="spaceBetween"
          onClick={onToggle}
          cursor="pointer"
          {...(isExpanded ? { paddingTop: 's8', paddingBottom: 's8' } : {})}
        >
          <Box flexDirection="row" alignItems="center" gap="s8">
            {isCompleted ? (
              <Box
                style={{
                  width: '20px',
                  height: '20px',
                  borderRadius: '50%',
                  backgroundColor: '#06838B',
                  flexShrink: '0',
                }}
                alignItems="center"
                justifyContent="center"
              >
                <F0Icon icon={Check} size="sm" color="#FFFFFF" />
              </Box>
            ) : (
              <Box
                style={{
                  width: '20px',
                  height: '20px',
                  borderRadius: '50%',
                  border: '1.5px dashed #011B4B73',
                  flexShrink: '0',
                }}
              />
            )}
            <Text.Normal size="200" weight="medium" stroked={isCompleted} color={'grey1000'}>
              {task.title}
            </Text.Normal>
            {!task.isMandatory && <F0TagRaw text={i18n.t('growth.get_started.optional')} />}
          </Box>
        </Box>
        <AnimatePresence initial={false}>
          {isExpanded && (
            <motion.div
              key="expanded"
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.25, ease: [0.04, 0.62, 0.23, 0.98] }}
              style={{ overflow: 'hidden' }}
            >
              <Box
                gap={isSmallScreen ? 's16' : 's32'}
                flexDirection="row"
                justifyContent="spaceBetween"
                flexWrap="wrapReverse"
              >
                <Box
                  flexDirection="column"
                  justifyContent="spaceBetween"
                  width="full"
                  style={{
                    maxWidth: '546px',
                  }}
                >
                  <Text size="200" color="grey800">
                    {task.description}
                  </Text>
                  <Box
                    flexDirection="row"
                    gap="s12"
                    justifyContent="spaceBetween"
                    alignItems="center"
                    paddingTop={isSmallScreen ? 's12' : undefined}
                  >
                    <Box flexDirection="row" gap="s12" alignItems="center">
                      {(effectiveTourId || actionUrl) && (
                        <F0Button
                          onClick={handleStartSetup}
                          variant="default"
                          label={i18n.t('growth.get_started.start_setup')}
                        />
                      )}
                      {(aiEnabled || task.helpCenterUrl) && (
                        <F0Button
                          onClick={handleLearnMore}
                          variant="outline"
                          label={i18n.t('growth.get_started.learn_more')}
                          icon={ExternalLink}
                        />
                      )}
                    </Box>
                    {!task.isMandatory && !isCompleted && onSkip && (
                      <F0Button
                        onClick={handleSkip}
                        variant="ghost"
                        label={i18n.t('growth.get_started.mark_as_done')}
                      />
                    )}
                  </Box>
                </Box>
                {task.videoUrl ? (
                  <VideoThumbnail
                    videoUrl={task.videoUrl}
                    title={task.title}
                    onOpen={() =>
                      track(trackingEvents.getStarted.openVideoTask, {
                        task_key: task.taskKey,
                        tour_id: effectiveTourId,
                        action_url: actionUrl,
                        is_completed: isCompleted,
                      })
                    }
                  />
                ) : (
                  <FallbackPlaceholder title={task.title} />
                )}
              </Box>
            </motion.div>
          )}
        </AnimatePresence>
      </Box>
    </Box>
  )
}

export default TaskItem
